<script setup>
import { useI18n } from 'vue-i18n';
import { replaceUnderscoreWithSpace } from './helper/filterHelper.js';

import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  appliedFilters: { type: Array, default: () => [] },
  maxVisibleFilters: { type: Number, default: 2 },
  clearButtonLabel: { type: String, default: '' },
  moreFiltersLabel: { type: String, default: '' },
  showClearButton: { type: Boolean, default: true },
});

const emit = defineEmits(['clearFilters', 'openFilter']);

const shouldCapitalizeFirstLetter = key => {
  const lowercaseKeys = ['email'];
  return !lowercaseKeys.includes(key);
};

// [PROSPEK] 2026-03-07 Localized attribute and operator labels for filter chips
const { t } = useI18n();

const RELATIVE_DATE_OPERATORS = {
  days_before: {
    fallback: '{attribute} before the last {value} days',
  },
  days_after: {
    fallback: '{attribute} within the last {value} days',
  },
};

const formatAttributeLabel = key => {
  if (!key) return '';
  const labels = {
    name: t('CONTACTS_FILTER.ATTRIBUTES.NAME'),
    email: t('CONTACTS_FILTER.ATTRIBUTES.EMAIL'),
    phone_number: t('CONTACTS_FILTER.ATTRIBUTES.PHONE_NUMBER'),
    identifier: t('CONTACTS_FILTER.ATTRIBUTES.IDENTIFIER'),
    city: t('CONTACTS_FILTER.ATTRIBUTES.CITY'),
    country: t('CONTACTS_FILTER.ATTRIBUTES.COUNTRY'),
    created_at: t('CONTACTS_FILTER.ATTRIBUTES.CREATED_AT'),
    last_activity: t('CONTACTS_FILTER.ATTRIBUTES.LAST_ACTIVITY'),
    last_activity_at: t('CONTACTS_FILTER.ATTRIBUTES.LAST_ACTIVITY'),
    referer_link: t('CONTACTS_FILTER.ATTRIBUTES.REFERER_LINK'),
    blocked: t('CONTACTS_FILTER.ATTRIBUTES.BLOCKED'),
    labels: t('CONTACTS_FILTER.ATTRIBUTES.LABELS'),
  };
  return labels[key] || replaceUnderscoreWithSpace(key);
};

const formatOperatorLabel = operator => {
  if (!operator) return '';
  const operators = {
    equal_to: t('CONTACTS_FILTER.OPERATOR_LABELS.equal_to'),
    not_equal_to: t('CONTACTS_FILTER.OPERATOR_LABELS.not_equal_to'),
    contains: t('CONTACTS_FILTER.OPERATOR_LABELS.contains'),
    does_not_contain: t('CONTACTS_FILTER.OPERATOR_LABELS.does_not_contain'),
    is_present: t('CONTACTS_FILTER.OPERATOR_LABELS.is_present'),
    is_not_present: t('CONTACTS_FILTER.OPERATOR_LABELS.is_not_present'),
    is_greater_than: t('CONTACTS_FILTER.OPERATOR_LABELS.is_greater_than'),
    is_less_than: t('CONTACTS_FILTER.OPERATOR_LABELS.is_lesser_than'),
    days_before: t('CONTACTS_FILTER.OPERATOR_LABELS.days_before'),
    days_after: t('CONTACTS_FILTER.OPERATOR_LABELS.days_after'),
  };
  return operators[operator] || replaceUnderscoreWithSpace(operator);
};
// [/PROSPEK]

const formatFilterValue = value => {
  // Case 1: null, undefined, empty string
  if (!value) return '';

  // Case 2: array → map each item, use name if present, else the item itself
  if (Array.isArray(value)) {
    return value.map(item => item?.name ?? item).join(', ');
  }

  // Case 3: object with a "name" property → return name
  // Case 4: primitive (string, number, etc.) → return as is
  return value?.name ?? value;
};

// [PROSPEK] 2026-06-20 Render relative-date filter chips as full phrases with interpolated values
const interpolateRelativeDateFallback = (template, attribute, value) =>
  template.replace('{attribute}', attribute).replace('{value}', value);

const isRelativeDateFilter = filter =>
  Boolean(RELATIVE_DATE_OPERATORS[filter.filterOperator]);

const getRelativeDateTranslation = (operator, attribute, value) => {
  if (operator === 'days_before') {
    return t('CONTACTS_FILTER.OPERATOR_PREVIEW.days_before', {
      attribute,
      value,
    });
  }

  return t('CONTACTS_FILTER.OPERATOR_PREVIEW.days_after', {
    attribute,
    value,
  });
};

const formatRelativeDateFilter = filter => {
  const attribute = formatAttributeLabel(filter.attributeKey);
  const value = formatFilterValue(filter.values);
  const preview = RELATIVE_DATE_OPERATORS[filter.filterOperator];
  const translated = getRelativeDateTranslation(
    filter.filterOperator,
    attribute,
    value
  );

  if (translated.startsWith('CONTACTS_FILTER.OPERATOR_PREVIEW.')) {
    return interpolateRelativeDateFallback(preview.fallback, attribute, value);
  }

  return translated;
};
// [/PROSPEK]
</script>

<template>
  <div class="flex flex-wrap items-center w-full gap-2 mx-auto">
    <template v-for="(filter, index) in appliedFilters" :key="index">
      <div
        v-if="index < maxVisibleFilters"
        class="inline-flex items-center gap-2 h-7"
      >
        <div
          class="flex items-center h-full min-w-0 gap-1 px-2 py-1 text-xs border rounded-lg hover:bg-n-solid-2 max-w-72 border-n-weak hover:cursor-pointer"
          @click="emit('openFilter')"
        >
          <!-- [PROSPEK] 2026-06-20 Keep relative-date chip preview semantics aligned with backend filters -->
          <template v-if="isRelativeDateFilter(filter)">
            <span
              :title="formatRelativeDateFilter(filter)"
              class="truncate text-n-slate-12"
            >
              {{ formatRelativeDateFilter(filter) }}
            </span>
          </template>
          <!-- [/PROSPEK] -->
          <template v-else>
            <span
              class="lowercase whitespace-nowrap first-letter:capitalize text-n-slate-12"
            >
              {{ formatAttributeLabel(filter.attributeKey) }}
            </span>
            <span class="px-1 text-xs text-n-slate-10 whitespace-nowrap">
              {{ formatOperatorLabel(filter.filterOperator) }}
            </span>
            <span
              v-if="filter.values"
              :title="formatFilterValue(filter.values)"
              class="lowercase truncate text-n-slate-12"
              :class="{
                'first-letter:capitalize': shouldCapitalizeFirstLetter(
                  filter.attributeKey
                ),
              }"
            >
              {{ formatFilterValue(filter.values) }}
            </span>
          </template>
        </div>
        <template
          v-if="
            index < maxVisibleFilters - 1 && index < appliedFilters.length - 1
          "
        >
          <span
            class="content-center h-full px-1 text-xs font-medium uppercase rounded-lg text-n-slate-10"
          >
            {{ filter.queryOperator }}
          </span>
        </template>
      </div>
    </template>
    <div
      v-if="appliedFilters.length > maxVisibleFilters"
      class="inline-flex items-center content-center px-1 text-xs rounded-lg text-n-slate-10 hover:text-n-slate-11 h-7 hover:cursor-pointer"
      @click="emit('openFilter')"
    >
      {{ moreFiltersLabel }}
    </div>
    <div v-if="showClearButton" class="w-px h-3 rounded-lg bg-n-strong" />
    <Button
      v-if="showClearButton"
      type="button"
      :label="clearButtonLabel"
      size="xs"
      class="!px-1"
      variant="ghost"
      @click="emit('clearFilters')"
    />
  </div>
</template>
